import { FeatureLink } from '@/components/common/FeatureLink';
import { IconSvg } from '@/components/common/icons/IconSvg';
import { Flex } from '@/components/common/layout/Flex';
import { Trunc } from '@/components/common/Trunc';
import { FeatureType, SITE_MENUS, type FeatureItem, FEATURE } from '@/config/features';
import { useBreakpoints } from '@/hooks/breakpoints/useBreakpoints';
import { Tooltip } from 'antd';
import { useSolidgateConditions } from '@feature/payment/solidgate/hooks/useSolidgateConditions';
import { useLocation } from 'react-router-dom';

// Styles
import cn from 'classnames';
import s from './Navigation.module.less';

type Props = {
  collapsed: boolean;
  onMenuClick?: (type: FeatureType) => void;
  className?: string;
};

const NavigationItem: RC<{
  collapsed: boolean;
  item: FeatureItem;
  onClick?: (type: FeatureType) => void;
}> = ({ item, onClick, collapsed }) => {
  const { pathname } = useLocation();
  const { isMd } = useBreakpoints();
  const { title, type, path, menu } = item;
  return (
    <li
      className={cn(s.menuItem, {
        [s.active]: pathname.includes(path),
      })}
    >
      <Tooltip title={!isMd && (menu?.heading ?? title)} placement={'right'}>
        <FeatureLink feature={type} className={s.menuItemLink} onClick={() => onClick?.(type)}>
          <Flex alignItems={'center'} gap={0} justifyContent={collapsed ? 'center' : 'flex-start'}>
            <IconSvg size={20} icon={menu?.icon || 'IcoRFileImage'} />
            <div className={s.menuItemText}>
              <Trunc text={menu?.heading ?? title} max={20} />
            </div>
          </Flex>
        </FeatureLink>
      </Tooltip>
    </li>
  );
};

export const Navigation = ({ onMenuClick, collapsed, className }: Props) => {
  const { canRenderPaymentCardsMenu } = useSolidgateConditions();
  return (
    <div
      className={cn(s.wrapper, className, {
        [s.collapsed]: collapsed,
      })}
    >
      {SITE_MENUS.map(({ name, list }) => (
        <div key={name} className={s.menu}>
          <div className={s.menuName}>{name}</div>
          <ul className={s.menuList}>
            {list.map((item) => (
              <NavigationItem key={item.type} item={item} onClick={onMenuClick} collapsed={collapsed} />
            ))}
          </ul>
        </div>
      ))}
      {/* Extra menu item (Solidgate payment cards)
       * NOTE: It wasn't good idea to add this item to Navigation component directly (anti SOLID principle),
       *       but, this menu item should be rendered only if payment provider is Solidgate (conditionally).
       * TODO: Remove this code after payment provider migration to Solidgate is completed.
       *       Move this item to the SITE_MENUS config.
       */}
      {canRenderPaymentCardsMenu && (
        <div className={s.menu}>
          <ul className={s.menuList} style={{ marginTop: -12 }}>
            <NavigationItem key="PaymentCards" item={FEATURE.PaymentCards} onClick={onMenuClick} collapsed={collapsed} />
          </ul>
        </div>
      )}
    </div>
  );
};
